import { createVisitor } from "@/services/internalApiV1";

// The internal API and the Action Cable connection both refuse a session-less
// request, so an anonymous public page needs a visitor before either can be
// used. Two consumers need that guarantee at different moments — `<WithVisitor>`
// before it renders, the shared cable before it connects — and a second visitor
// would be minted if they each created one. Memoizing the in-flight promise
// makes the first caller do the work and the rest wait for it.
let pending: Promise<void> | null = null;

async function establish(): Promise<void> {
  const userStore = useUserStore();

  if (userStore.hasSession) return;

  const visitor = await createVisitor();

  if (isVisitor(visitor)) {
    userStore.visitorToken = visitor.visitorToken;
  }

  await userStore.refresh();
}

/**
 * Resolve once the tab has a session, creating a visitor if it has none.
 * Safe to call concurrently and repeatedly; only the first call that finds no
 * session issues a request.
 */
export function ensureVisitorSession(): Promise<void> {
  pending ??= establish().catch((error: unknown) => {
    // Drop the memo so a later caller can retry rather than inheriting the
    // failure for the tab's lifetime.
    pending = null;
    throw error;
  });

  return pending;
}

/**
 * Forget the tab's visitor so the next `ensureVisitorSession` mints a new one.
 * For a visitor the server no longer accepts — its session expired, or
 * `RemoveOldVisitorsJob` deleted it — which the memo would otherwise keep
 * answering for the tab's lifetime.
 */
export function forgetVisitorSession(): void {
  const userStore = useUserStore();

  pending = null;
  userStore.user = undefined;
  userStore.visitorToken = undefined;
}
